/**
 * Phreatic Billing Page — Acre Tokens + Well Subscription
 */
import { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { FadeIn } from '../../components/animations/FadeIn';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { ArgosCheckout } from '../../components/ArgosCheckout';

// Section-anchored packs: 1 token = 1 acre, 1 section = 640 acres ≈ $400 retail
// ($0.625/token). Volume drops the per-token price (declining wholesale ladder).
const TOKEN_TIERS = [
  { tokens: 6400,    pricePerToken: 0.625, label: 'Starter' },       // 10 sections
  { tokens: 32000,   pricePerToken: 0.50,  label: 'Growth' },        // 50 sections
  { tokens: 128000,  pricePerToken: 0.35,  label: 'Professional' },  // 200 sections
  { tokens: 640000,  pricePerToken: 0.20,  label: 'Enterprise' },    // 1,000 sections
  { tokens: 3200000, pricePerToken: 0.10,  label: 'Institutional' }, // 5,000 sections
];


interface TokenBalance {
  balance: number;
  total_purchased: number;
  total_used: number;
  total_expired: number;
  last_purchase_at: string | null;
  last_usage_at: string | null;
}

interface LedgerEntry {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
  created_by_email?: string | null;
  metadata: any;
}

interface Subscription {
  id?: string;
  status: string;
  well_count: number;
  monthly_rate_per_well?: number;
  monthly_total: number;
  wells: any[];
  billing_day?: number;
  current_period?: { start: string | null; end: string | null };
}

export function PhreaticBilling() {
  // Token balance is a shared org resource — every seat sees it. Only admins
  // can spend (buy tokens / unlock dashboard / subscriptions); the backend
  // enforces this, so we also hide the purchase controls for non-admins.
  const effectiveRole = useAuthStore((s) => s.getEffectiveRole());
  const isAdmin = effectiveRole === 'admin' || effectiveRole === 'superadmin';
  const [balance, setBalance] = useState<TokenBalance | null>(null);
  const [history, setHistory] = useState<LedgerEntry[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Purchase state
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(128000); // default to a valid pack size (Professional)
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseMsg, setPurchaseMsg] = useState<string | null>(null);
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);
  const [checkoutReturn, setCheckoutReturn] = useState<string>('');
  const [checkoutTitle, setCheckoutTitle] = useState<string>('Complete Payment');
  const [checkoutAmount, setCheckoutAmount] = useState<number | undefined>(undefined);
  const [checkoutLabel, setCheckoutLabel] = useState<string | undefined>(undefined);
  const [checkoutInterval, setCheckoutInterval] = useState<string | null>(null);
  const [bdInquiry, setBdInquiry] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleBlueDiamondInquiry = async () => {
    setBdInquiry('sending');
    try {
      await apiClient.post('/phreatic/billing/blue-diamond-inquiry');
      setBdInquiry('sent');
    } catch {
      setBdInquiry('error');
    }
  };

  // License / plan
  const [license, setLicense] = useState<{
    effective_tier: string; license_tier: string;
    seats_purchased: number; dashboard_license_expires_at: string | null;
  } | null>(null);
  const [upgrading, setUpgrading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [balResp, histResp, subResp, licResp] = await Promise.all([
        apiClient.get('/phreatic/billing/tokens/balance'),
        apiClient.get('/phreatic/billing/tokens/history?per_page=10'),
        apiClient.get('/phreatic/billing/subscription'),
        apiClient.get('/phreatic/billing/license').catch(() => null),
      ]);
      setBalance(balResp.data);
      setHistory(histResp.data.entries || []);
      setSubscription(subResp.data);
      if (licResp) setLicense(licResp.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returned from the in-app checkout (token pack / dashboard license)?
    // Confirm + activate immediately (same idempotent path as the webhook),
    // strip the intent id, then load fresh balances/license.
    const pi = new URLSearchParams(window.location.search).get('payment_intent');
    if (pi) {
      apiClient.post('/auth/payment/confirm', { payment_intent_id: pi })
        .catch(() => {})
        .finally(() => {
          window.history.replaceState({}, '', '/phreatic/billing');
          loadData();
        });
    } else {
      loadData();
    }
  }, [loadData]);

  const handlePurchase = async () => {
    if (purchaseAmount <= 0) return;
    setPurchasing(true);
    setPurchaseMsg(null);
    try {
      // Real Stripe Embedded Checkout — `tokens` must be an exact pack size
      // (the tiers below are the valid packs). On payment Stripe returns to the
      // billing page, where confirm-on-return + the webhook credit the balance.
      const resp = await apiClient.post('/phreatic/billing/checkout', {
        sku: 'tokens',
        tokens: purchaseAmount,
      });
      if (resp.data?.client_secret) {
        setShowPurchase(false);
        setCheckoutReturn(`${window.location.origin}/phreatic/billing?purchase=success`);
        setCheckoutTitle('Buy Acre Tokens');
        setCheckoutAmount(resp.data.amount);
        setCheckoutLabel(resp.data.label);
        setCheckoutInterval(resp.data.interval ?? null);
        setCheckoutSecret(resp.data.client_secret);
        return;
      }
      setPurchaseMsg('Could not start checkout.');
    } catch (err: any) {
      setPurchaseMsg(err?.response?.data?.detail || 'Checkout failed');
    } finally {
      setPurchasing(false);
    }
  };

  const handleBuyDashboard = async () => {
    setUpgrading(true);
    setPurchaseMsg(null);
    try {
      const resp = await apiClient.post('/phreatic/billing/checkout', {
        sku: 'dashboard_license',
        seats: license?.seats_purchased || 1,
      });
      if (resp.data?.client_secret) {
        setCheckoutReturn(`${window.location.origin}/phreatic/billing?upgrade=success`);
        setCheckoutTitle('Dashboard License');
        setCheckoutAmount(resp.data.amount);
        setCheckoutLabel(resp.data.label);
        setCheckoutInterval(resp.data.interval ?? null);
        setCheckoutSecret(resp.data.client_secret);
        return;
      }
      setPurchaseMsg('Could not start dashboard checkout.');
    } catch (err: any) {
      setPurchaseMsg(err?.response?.data?.detail || 'Dashboard checkout failed');
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="w-6 h-6 text-mono-400 animate-spin" />
        <span className="ml-2 text-mono-400 text-sm">Loading billing...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {checkoutSecret && (
        <ArgosCheckout
          clientSecret={checkoutSecret}
          returnUrl={checkoutReturn}
          title={checkoutTitle}
          label={checkoutLabel}
          amount={checkoutAmount}
          interval={checkoutInterval}
          onClose={() => setCheckoutSecret(null)}
        />
      )}
      <FadeIn>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-mono-100">Billing</h1>
            <p className="text-mono-400 text-sm mt-1">Acre tokens + well subscriptions</p>
          </div>
        </div>
      </FadeIn>

      {/* ── PLAN / LICENSE ── */}
      {license && (
        <FadeIn delay={0.05}>
          <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] rounded-lg p-6 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-10">
              <div>
                <div className="text-[10px] text-mono-500 uppercase tracking-wider">Plan</div>
                <div className="text-lg font-semibold text-mono-100 font-orbitron">
                  {license.effective_tier === 'dashboard' ? 'Dashboard License'
                    : license.effective_tier === 'command' ? 'Onboarded — CARI Report Templates'
                    : 'Not Onboarded'}
                </div>
                <div className="text-[10px] text-mono-500 mt-1 max-w-xs">
                  {license.effective_tier === 'dashboard'
                    ? 'Full site dashboards + interactive CARI agent unlocked.'
                    : license.effective_tier === 'command'
                    ? 'CARI report templates on each site. Upgrade for full dashboards + interactive CARI.'
                    : 'Complete onboarding to access Command + CARI report templates.'}
                </div>
                {license.effective_tier === 'dashboard' && license.dashboard_license_expires_at && (
                  <div className="text-[10px] text-mono-500 mt-1">
                    Renews {new Date(license.dashboard_license_expires_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] text-mono-500 uppercase tracking-wider">Seats</div>
                <div className="text-lg font-semibold text-mono-100">{license.seats_purchased}</div>
              </div>
            </div>
            {license.effective_tier !== 'dashboard' && isAdmin && (
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={handleBuyDashboard}
                  disabled={upgrading}
                  className="px-4 py-2 text-sm rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50"
                >
                  {upgrading ? 'Starting checkout…' : 'Unlock Full Dashboards + CARI'}
                </button>
                <span className="text-[10px] text-mono-500">Full dashboards + interactive CARI agent</span>
              </div>
            )}
          </div>
        </FadeIn>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">{error}</div>
      )}

      {purchaseMsg && (
        <div className={`rounded-lg p-3 text-sm ${purchaseMsg.includes('added') ? 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
          {purchaseMsg}
        </div>
      )}

      {/* ── ACRE TOKENS ── */}
      <FadeIn delay={0.1}>
        <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-mono-100">Acre Tokens</h2>
            {isAdmin && (
              <button
                onClick={() => setShowPurchase(!showPurchase)}
                className="px-4 py-2 text-sm rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30"
              >
                {showPurchase ? 'Cancel' : 'Buy Tokens'}
              </button>
            )}
          </div>

          {/* Balance */}
          {balance && (
            <div className="text-center mb-6">
              <div className="text-4xl font-bold text-cyan-400">{balance.balance.toLocaleString()}</div>
              <div className="text-mono-500 text-sm mt-1">tokens available</div>
              <div className="text-mono-600 text-xs mt-2">
                {balance.total_purchased.toLocaleString()} purchased
                {' · '}{balance.total_used.toLocaleString()} used
                {balance.total_expired > 0 && <> · {balance.total_expired.toLocaleString()} expired</>}
              </div>
              {balance.total_purchased > 0 && (
                <div className="mt-3 w-full max-w-md mx-auto">
                  <div className="h-1.5 bg-mono-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-cyan-500/50 rounded-full"
                      style={{ width: `${Math.min(100, (balance.total_used / Math.max(balance.total_purchased, 1)) * 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-mono-600 mt-1">
                    {((balance.total_used / Math.max(balance.total_purchased, 1)) * 100).toFixed(1)}% used
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Purchase Panel — Wholesale Tiers */}
          {showPurchase && (
            <div className="bg-mono-800/50 rounded-lg p-4 mb-4 border border-cyan-500/20">
              <div className="text-sm text-mono-300 mb-3">Select a pack — 1 token = 1 acre · 1 section = 640 acres</div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
                {TOKEN_TIERS.map(tier => {
                  const total = tier.tokens * tier.pricePerToken;
                  const isSelected = purchaseAmount === tier.tokens;
                  return (
                    <button key={tier.tokens}
                      onClick={() => setPurchaseAmount(tier.tokens)}
                      className={`rounded-lg p-3 text-left transition-all border ${
                        isSelected
                          ? 'border-cyan-500/60 bg-cyan-500/10'
                          : 'border-mono-700 bg-mono-800/50 hover:border-mono-500'
                      }`}
                    >
                      <div className="text-[10px] text-mono-500 uppercase tracking-wider">{tier.label}</div>
                      <div className="text-lg font-bold text-mono-100">{(tier.tokens / 1000).toLocaleString()}K <span className="text-xs font-normal text-mono-400">tokens</span></div>
                      <div className="text-[10px] text-mono-500">{(tier.tokens / 640).toLocaleString()} sections</div>
                      <div className="text-xs text-cyan-400 font-semibold">${tier.pricePerToken.toFixed(2)}/token</div>
                      <div className="text-[10px] text-mono-500 mt-1">${total.toLocaleString()}</div>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-mono-400">
                  <span className="text-mono-300 font-semibold">{(purchaseAmount / 640).toLocaleString()} sections</span> · {purchaseAmount.toLocaleString()} tokens × ${TOKEN_TIERS.find(t => t.tokens === purchaseAmount)?.pricePerToken.toFixed(2) || '—'}/token = {' '}
                  <span className="text-cyan-400 font-semibold">
                    ${(purchaseAmount * (TOKEN_TIERS.find(t => t.tokens === purchaseAmount)?.pricePerToken || 0)).toLocaleString()}
                  </span>
                </div>
                <button
                  onClick={handlePurchase}
                  disabled={purchasing || purchaseAmount <= 0}
                  className="px-4 py-2 text-sm rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50"
                >
                  {purchasing ? 'Processing...' : `Purchase ${(purchaseAmount / 1000).toLocaleString()}K Tokens`}
                </button>
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div>
              <div className="text-xs text-mono-500 uppercase tracking-wider mb-2">Recent Activity</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-mono-400 border-b border-mono-800">
                    <th className="text-left py-1.5 px-2">Date</th>
                    <th className="text-left py-1.5 px-2">Type</th>
                    <th className="text-left py-1.5 px-2">Description</th>
                    <th className="text-left py-1.5 px-2">By</th>
                    <th className="text-right py-1.5 px-2">Amount</th>
                    <th className="text-right py-1.5 px-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(e => (
                    <tr key={e.id} className="border-b border-mono-800/50 text-mono-300">
                      <td className="py-1.5 px-2 text-mono-500">
                        {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-1.5 px-2">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                          e.type === 'purchase' ? 'bg-cyan-500/10 text-cyan-400'
                          : e.type === 'usage' ? 'bg-cyan-500/10 text-cyan-400'
                          : e.type === 'refund' ? 'bg-cyan-500/10 text-cyan-400'
                          : 'bg-mono-800 text-mono-400'
                        }`}>
                          {e.type}
                        </span>
                      </td>
                      <td className="py-1.5 px-2">{e.description}</td>
                      <td className="py-1.5 px-2 text-mono-400">{e.created_by_email || '—'}</td>
                      <td className={`text-right py-1.5 px-2 font-semibold ${e.amount > 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                        {e.amount > 0 ? '+' : ''}{e.amount.toLocaleString()}
                      </td>
                      <td className="text-right py-1.5 px-2 text-mono-400">{e.balance_after.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </FadeIn>

      {/* ── WELL SUBSCRIPTION ── */}
      <FadeIn delay={0.2}>
        <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-mono-100">Pump Intelligence</h2>
              {subscription && subscription.status !== 'none' && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                  subscription.status === 'active' ? 'bg-cyan-500/10 text-cyan-400'
                  : subscription.status === 'paused' ? 'bg-cyan-500/10 text-cyan-400'
                  : 'bg-mono-800 text-mono-400'
                }`}>
                  {subscription.status.toUpperCase()}
                </span>
              )}
            </div>
          </div>

          {subscription && subscription.status !== 'none' ? (
            <div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-mono-800/50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-cyan-400">{subscription.well_count}</div>
                  <div className="text-mono-500 text-sm">wells</div>
                </div>
                <div className="bg-mono-800/50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-mono-100">${subscription.monthly_total.toLocaleString()}</div>
                  <div className="text-mono-500 text-sm">per month</div>
                </div>
              </div>

              {subscription.wells && subscription.wells.length > 0 && (
                <div>
                  <div className="text-xs text-mono-500 uppercase tracking-wider mb-2">Subscribed Wells</div>
                  <div className="space-y-1">
                    {subscription.wells.map((w: any, i: number) => (
                      <div key={i} className="flex items-center justify-between bg-mono-800/30 rounded px-3 py-1.5 text-xs">
                        <span className="text-mono-300">{w.well_name || w.well_id}</span>
                        <span className="text-mono-600">${(subscription.monthly_rate_per_well || 500).toLocaleString()}/mo</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <CurrencyDollarIcon className="w-10 h-10 text-cyan-500/50 mx-auto mb-3" />
              <div className="inline-block mb-2 px-2 py-0.5 rounded text-[10px] font-semibold bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 tracking-wider">BETA</div>
              <div className="text-mono-300 text-sm">Pump Control is currently in beta testing</div>
              <div className="text-mono-500 text-xs mt-1">Live telemetry &amp; pump optimization are being piloted. Contact us to join the beta.</div>
            </div>
          )}
        </div>
      </FadeIn>

      {/* ── BLUE DIAMOND ACCESS ── */}
      <FadeIn delay={0.3}>
        <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] rounded-lg p-6 relative overflow-hidden">
          {/* gw-topology background */}
          <div className="absolute inset-0 bg-cover bg-center opacity-[0.06]"
            style={{ backgroundImage: 'url(/gw-topology.jpg)' }} />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-mono-100">Blue Diamond Access</h2>
                <p className="text-mono-500 text-xs mt-1">Statewide Texas groundwater intelligence — sat thickness heatmap with V5 time horizons</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-mono-800/50 rounded-lg p-4 flex flex-col justify-center">
                <div className="text-lg font-bold text-cyan-300">Enterprise</div>
                <div className="text-mono-500 text-sm">Custom pricing — contact sales</div>
              </div>
              <div className="bg-mono-800/50 rounded-lg p-4">
                <div className="text-sm text-mono-300">Includes:</div>
                <ul className="text-xs text-mono-400 mt-1 space-y-1">
                  <li>84K+ wells across 254 Texas counties</li>
                  <li>V5 predicted heatmaps at 6 time horizons</li>
                  <li>County + aquifer statistics</li>
                  <li>Permanent access — no subscription</li>
                </ul>
              </div>
              <div className="flex flex-col items-center justify-center gap-2">
                {bdInquiry === 'sent' ? (
                  <span className="text-xs text-cyan-400 text-center">Request sent — our sales team will reach out.</span>
                ) : (
                  <button
                    onClick={handleBlueDiamondInquiry}
                    disabled={bdInquiry === 'sending'}
                    className="px-6 py-3 text-sm rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 font-semibold disabled:opacity-50"
                  >
                    {bdInquiry === 'sending' ? 'Sending…' : 'Contact Sales'}
                  </button>
                )}
                {bdInquiry === 'error' && (
                  <span className="text-[10px] text-red-400 text-center">Could not send — try again.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </FadeIn>
    </div>
  );
}
